// src/pages/LawyerProfilePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Upload,
  Avatar,
  Typography,
  Row,
  Col,
  Button,
  message,
  Skeleton,
} from "antd";
import { UserOutlined, UploadOutlined, EditOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchAllLawyers,
  fetchCountries,
  fetchStates,
  fetchCities,
  fetchFirms,
  updateLawyer,
} from "../../../api/lawyerApi";
import { withBase } from "../../../config/env";


const { Title, Text } = Typography;

// ---------- Helpers ----------
function safeGetLoggedInUserId() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return (
      user?.id ??
      user?.user_id ??
      user?.data?.id ??
      user?.data?.user_id ??
      null
    );
  } catch {
    return null;
  }
}

const toDayjsOrNull = (val) => (val ? dayjs(val) : null);

const buildImageUrl = (p) => {
  if (!p) return null;
  if (/^https?:\/\//i.test(p)) return p; // already absolute URL
  const clean = String(p).replace(/^\/+/, "").replace(/\\/g, "/");
  return withBase(clean);
};

const fieldLabel = {
  name: "Full Name",
  gender: "Gender",
  email: "Email",
  mobile: "Mobile",
  postal_code: "Postal Code",
  address: "Address",
  country_id: "Country",
  state_id: "State",
  city_id: "City",
  date_of_birth: "Date of Birth",
  cnic_no: "CNIC",
  license_number: "License Number",
  license_issue_date: "License Issue Date",
  license_expiry_date: "License Expiry Date",
  specialization: "Specialization",
  firm_id: "Firm",
  image: "Profile Image",
  status: "Status",
};

export default function LawyerProfilePage() {
  const [form] = Form.useForm();

  const userId = useMemo(safeGetLoggedInUserId, []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [lawyer, setLawyer] = useState(null);

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [firms, setFirms] = useState([]);

  // New upload preview + list
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [dbImgErrored, setDbImgErrored] = useState(false);

  // ------- Initial data load -------
  useEffect(() => {
    let mounted = true;

    async function load() {
      if (!userId) {
        message.error("User not found in local storage. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Load lookups in parallel
        const [countriesRes, firmsRes] = await Promise.all([
          fetchCountries(),
          fetchFirms(),
        ]);

        if (!mounted) return;

        setCountries(countriesRes || []);
        setFirms(firmsRes || []);

        // Find the logged-in lawyer
        const list = await fetchAllLawyers();
        if (!mounted) return;

        const found =
          list.find((l) => String(l.user_id) === String(userId)) ||
          list.find((l) => String(l.id) === String(userId)) ||
          null;

        if (!found) {
          message.error("Lawyer profile not found.");
          setLoading(false);
          return;
        }

        setLawyer(found);

        // Load dependent dropdowns based on existing selections
        if (found.country_id) {
          const stateList = await fetchStates(found.country_id);
          if (!mounted) return;
          setStates(stateList);
        }
        if (found.state_id) {
          const cityList = await fetchCities(found.state_id);
          if (!mounted) return;
          setCities(cityList);
        }

        // Set initial form values
        form.setFieldsValue({
          ...found,
          date_of_birth: toDayjsOrNull(found.date_of_birth),
          license_issue_date: toDayjsOrNull(found.license_issue_date),
          license_expiry_date: toDayjsOrNull(found.license_expiry_date),
        });

        setDbImgErrored(false);
      } catch (e) {
        console.error(e);
        message.error("Failed to load profile.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [userId, form]);

  // ------- Dropdown change handlers -------
  const onCountryChange = async (countryId) => {
    form.setFieldsValue({ state_id: undefined, city_id: undefined });
    setStates([]);
    setCities([]);
    if (!countryId) return;
    try {
      const stateList = await fetchStates(countryId);
      setStates(stateList);
    } catch (e) {
      console.error(e);
      message.error("Failed to load states.");
    }
  };

  const onStateChange = async (stateId) => {
    form.setFieldsValue({ city_id: undefined });
    setCities([]);
    if (!stateId) return;
    try {
      const cityList = await fetchCities(stateId);
      setCities(cityList);
    } catch (e) {
      console.error(e);
      message.error("Failed to load cities.");
    }
  };

  // ------- Image upload/preview -------
  const beforeUpload = (file) => {
    const isImage =
      file.type === "image/jpeg" ||
      file.type === "image/png" ||
      file.type === "image/webp" ||
      file.type === "image/jpg";
    if (!isImage) {
      message.error("Only JPG/PNG/WEBP files are allowed.");
      return Upload.LIST_IGNORE;
    }
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Image must be smaller than 5MB.");
      return Upload.LIST_IGNORE;
    }

    // Read file to dataURL for preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      setAvatarPreview(dataUrl);
      // Make Upload show the real image thumbnail (not icon)
      setFileList([
        {
          uid: String(Date.now()),
          name: file.name || "avatar",
          status: "done",
          url: dataUrl,
          thumbUrl: dataUrl,
        },
      ]);
    };
    reader.readAsDataURL(file);

    setAvatarFile(file);
    return false; // prevent auto upload
  };

  // ------- Submit -------
  const onFinish = async (values) => {
    if (!userId) {
      message.error("User not found. Please log in again.");
      return;
    }
  
    try {
      setSaving(true);
  
      // Build multipart form data
      const formData = new FormData();
      const appendIfPresent = (k, v) => {
        if (v !== undefined && v !== null && v !== "") formData.append(k, v);
      };
  
      // Scalars
      appendIfPresent("name", values.name);
      appendIfPresent("gender", values.gender);
      appendIfPresent("email", values.email);
      appendIfPresent("mobile", values.mobile);
      appendIfPresent("postal_code", values.postal_code);
      appendIfPresent("address", values.address);
      appendIfPresent("country_id", values.country_id);
      appendIfPresent("state_id", values.state_id);
      appendIfPresent("city_id", values.city_id);
      appendIfPresent("cnic_no", values.cnic_no);
      appendIfPresent("license_number", values.license_number);
      appendIfPresent("specialization", values.specialization);
      appendIfPresent("status", values.status);
      appendIfPresent("firm_id", values.firm_id);
  
      // Dates (YYYY-MM-DD)
      appendIfPresent(
        "date_of_birth",
        values.date_of_birth ? values.date_of_birth.format("YYYY-MM-DD") : null
      );
      appendIfPresent(
        "license_issue_date",
        values.license_issue_date ? values.license_issue_date.format("YYYY-MM-DD") : null
      );
      appendIfPresent(
        "license_expiry_date",
        values.license_expiry_date ? values.license_expiry_date.format("YYYY-MM-DD") : null
      );
  
      // Image (if picked)
      if (avatarFile) formData.append("image", avatarFile);
  
      // Call API
      const apiRes = await updateLawyer(userId, formData);
      const payload = apiRes?.data ?? apiRes; // normalize axios vs direct
  
      if (payload?.status_code === 200) {
        // Update localStorage (preserve existing token)
        const oldUser = JSON.parse(localStorage.getItem("user")) || {};
        const { id, name, email: userEmail, role, status, image } = payload.data || {};
  
        localStorage.setItem(
          "user",
          JSON.stringify({
            id,
            name,
            email: userEmail,
            role,
            status,
            image,
            token: oldUser.token || null,
          })
        );
  
        // Update UI state (optional but nice: reflect new values immediately)
        setLawyer((prev) => ({
          ...(prev || {}),
          ...values,
          image: image ?? (prev ? prev.image : null),
        }));
  
        // If you want to keep preview visible until reload, don't clear it.
        // If you prefer clearing after successful save, uncomment below:
        // setAvatarPreview(null);
        // setAvatarFile(null);
        // setFileList([]);
  
        message.success(payload.message || "Profile updated successfully.");
      } else {
        message.error(payload?.message || "Failed to update profile.");
      }
    } catch (e) {
      console.error(e);
      message.error("Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };
  

  // ------- Derived image URL for DB image (+cache-buster) -------
  const dbImage = lawyer?.image
    ? `${buildImageUrl(lawyer.image)}?v=${lawyer?.updated_at || ""}`
    : null;

  // ------- UI -------
  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 16px" }}>
      <Card
        style={{
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          border: "1px solid #f0f0f0",
        }}
        bodyStyle={{ padding: 0 }}
      >
        {/* Header */}
        <div
          style={{
            background: "#f9f9f9",
            padding: "24px",
            display: "flex",
            alignItems: "center",
            gap: 24,
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          {/* Left: Text */}
          <div style={{ flex: 1 }}>
            <Title level={3} style={{ margin: 0, fontWeight: 600 }}>
              {lawyer?.name || "Edit Profile"}
            </Title>
            <Text type="secondary" style={{ fontSize: 15 }}>
              Update your personal, license, and address details
            </Text>
          </div>

          {/* Right: Avatars */}
          <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
            {/* Current (DB) Avatar */}
            <div style={{ textAlign: "center" }}>
              <Avatar
                size={90}
                src={!dbImgErrored ? dbImage : null}
                onError={() => {
                  setDbImgErrored(true);
                  return false; // let Avatar fallback
                }}
                icon={dbImgErrored && <UserOutlined />}
                style={{
                  border: "3px solid #fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }}
              />
              <Text style={{ display: "block", marginTop: 8, fontSize: 12 }}>
                Current Photo
              </Text>
            </div>

            {/* New (Preview) Avatar — only when a file is selected */}
            {avatarPreview && (
              <div style={{ textAlign: "center" }}>
                <Avatar
                  size={90}
                  src={avatarPreview}
                  style={{
                    border: "3px solid #1890ff",
                    boxShadow: "0 2px 8px rgba(24,144,255,0.2)"
                  }}
                />
                <Text style={{
                  display: "block",
                  marginTop: 8,
                  fontSize: 12,
                  color: "#1890ff"
                }}>
                  New Photo
                </Text>
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: "24px" }}>
          {loading ? (
            <Skeleton active paragraph={{ rows: 8 }} />
          ) : (
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{ status: "Active", gender: "Male" }}
            >
              {/* Avatar Upload Section */}
              <Card
                size="small"
                style={{
                  borderRadius: 8,
                  marginBottom: 24,
                  border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: "16px 24px" }}
              >
                <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
                  <EditOutlined style={{
                    color: "#1890ff",
                    fontSize: 18,
                    marginRight: 8
                  }} />
                  <Title level={5} style={{ margin: 0 }}>
                    Profile Photo
                  </Title>
                </div>

                <Upload
                  accept="image/*"
                  listType="picture-card"
                  showUploadList
                  beforeUpload={beforeUpload}
                  fileList={fileList}
                  maxCount={1}
                  onRemove={() => {
                    setAvatarPreview(null);
                    setAvatarFile(null);
                    setFileList([]);
                  }}
                >
                  {fileList.length === 0 && (
                    <div style={{ padding: 8 }}>
                      <UploadOutlined style={{ fontSize: 20 }} />
                      <div style={{ marginTop: 8 }}>Upload Photo</div>
                    </div>
                  )}
                </Upload>

                <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                  Recommended size: 500x500px. JPG, PNG or WEBP (max 5MB)
                </Text>
              </Card>

              {/* Personal Info */}
              <Card
                size="small"
                style={{
                  borderRadius: 8,
                  marginBottom: 24,
                  border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: "16px 24px" }}
              >
                <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
                  <EditOutlined style={{
                    color: "#1890ff",
                    fontSize: 18,
                    marginRight: 8
                  }} />
                  <Title level={5} style={{ margin: 0 }}>
                    Personal Information
                  </Title>
                </div>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.name}</span>}
                      name="name"
                      rules={[{ required: true, message: "Please enter name" }]}
                    >
                      <Input
                        placeholder="Enter full name"
                        style={{ borderRadius: 6 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.gender}</span>}
                      name="gender"
                    >
                      <Select
                        style={{ borderRadius: 6 }}
                        options={[
                          { value: "Male", label: "Male" },
                          { value: "Female", label: "Female" },
                          { value: "Other", label: "Other" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.email}</span>}
                      name="email"
                      rules={[{ type: "email", message: "Invalid email" }]}
                    >
                      <Input
                        placeholder="name@email.com"
                        style={{ borderRadius: 6 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.mobile}</span>}
                      name="mobile"
                    >
                      <Input
                        placeholder="+92 3xx xxxxxxx"
                        style={{ borderRadius: 6 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.date_of_birth}</span>}
                      name="date_of_birth"
                    >
                      <DatePicker
                        style={{ width: '100%', borderRadius: 6 }}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.cnic_no}</span>}
                      name="cnic_no"
                    >
                      <Input
                        placeholder="CNIC number"
                        style={{ borderRadius: 6 }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* License Info */}
              <Card
                size="small"
                style={{
                  borderRadius: 8,
                  marginBottom: 24,
                  border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: "16px 24px" }}
              >
                <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
                  <EditOutlined style={{
                    color: "#1890ff",
                    fontSize: 18,
                    marginRight: 8
                  }} />
                  <Title level={5} style={{ margin: 0 }}>
                    License Information
                  </Title>
                </div>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.license_number}</span>}
                      name="license_number"
                    >
                      <Input
                        placeholder="License number"
                        style={{ borderRadius: 6 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.specialization}</span>}
                      name="specialization"
                    >
                      <Input
                        placeholder="e.g., Corporate Law, Family Law"
                        style={{ borderRadius: 6 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.license_issue_date}</span>}
                      name="license_issue_date"
                    >
                      <DatePicker
                        style={{ width: '100%', borderRadius: 6 }}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.license_expiry_date}</span>}
                      name="license_expiry_date"
                    >
                      <DatePicker
                        style={{ width: '100%', borderRadius: 6 }}
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Firm & Status */}
              <Card hidden
                size="small"
                style={{
                  borderRadius: 8,
                  marginBottom: 24,
                  border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: "16px 24px" }}
              >
                <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
                  <EditOutlined style={{
                    color: "#1890ff",
                    fontSize: 18,
                    marginRight: 8
                  }} />
                  <Title level={5} style={{ margin: 0 }}>
                    Professional Details
                  </Title>
                </div>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.firm_id}</span>}
                      name="firm_id"
                    >
                      <Select
                        style={{ borderRadius: 6 }}
                        placeholder="Select firm"
                        allowClear
                        options={(firms || []).map((f) => ({
                          value: f.id,
                          label: f.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.status}</span>}
                      name="status"
                    >
                      <Select
                        style={{ borderRadius: 6 }}
                        options={[
                          { value: "Active", label: "Active" },
                          { value: "Inactive", label: "Inactive" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Address */}
              <Card
                size="small"
                style={{
                  borderRadius: 8,
                  marginBottom: 24,
                  border: "1px solid #f0f0f0",
                }}
                bodyStyle={{ padding: "16px 24px" }}
              >
                <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
                  <EditOutlined style={{
                    color: "#1890ff",
                    fontSize: 18,
                    marginRight: 8
                  }} />
                  <Title level={5} style={{ margin: 0 }}>
                    Address Information
                  </Title>
                </div>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.country_id}</span>}
                      name="country_id"
                    >
                      <Select
                        style={{ borderRadius: 6 }}
                        showSearch
                        placeholder="Select country"
                        optionFilterProp="label"
                        onChange={onCountryChange}
                        options={(countries || []).map((c) => ({
                          value: c.id,
                          label: c.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.state_id}</span>}
                      name="state_id"
                    >
                      <Select
                        style={{ borderRadius: 6 }}
                        showSearch
                        placeholder="Select state"
                        optionFilterProp="label"
                        onChange={onStateChange}
                        options={(states || []).map((s) => ({
                          value: s.id,
                          label: s.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.city_id}</span>}
                      name="city_id"
                    >
                      <Select
                        style={{ borderRadius: 6 }}
                        showSearch
                        placeholder="Select city"
                        optionFilterProp="label"
                        options={(cities || []).map((c) => ({
                          value: c.id,
                          label: c.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.postal_code}</span>}
                      name="postal_code"
                    >
                      <Input
                        placeholder="e.g., 54000"
                        style={{ borderRadius: 6 }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      label={<span style={{ fontWeight: 500 }}>{fieldLabel.address}</span>}
                      name="address"
                    >
                      <Input.TextArea
                        rows={3}
                        placeholder="Street, area, etc."
                        style={{ borderRadius: 6 }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              {/* Form Actions */}
              <div style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 16,
                paddingTop: 16,
                borderTop: "1px solid #f0f0f0"
              }}>
                <Button
                  style={{ borderRadius: 6 }}
                  onClick={() => {
                    form.resetFields();
                    setAvatarPreview(null);
                    setAvatarFile(null);
                    setFileList([]);
                    setDbImgErrored(false);
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  style={{
                    borderRadius: 6,
                    padding: "0 24px",
                    height: 40
                  }}
                >
                  Save Changes
                </Button>
              </div>
            </Form>
          )}
        </div>
      </Card>
    </div>
  );
}